package songwritersnotebook.app.utils

import songwritersnotebook.app.di.PlatformDependencyProvider

actual object AudioSampleLoader {
    actual val nativeSampleIdCache: MutableMap<String, Int> = mutableMapOf()
    private val sampleIdMap = mutableMapOf<Int, String>()
    private var nextId = 1

    actual suspend fun loadSoundFromCache(assetPath: String): Int? {
        if (nativeSampleIdCache.containsKey(assetPath)) {
            return nativeSampleIdCache[assetPath]
        }

        val id = nextId++
        nativeSampleIdCache[assetPath] = id
        sampleIdMap[id] = assetPath
        
        // Trigger load in AudioHandler
        val instrument = assetPath.substringBefore("/")
        val note = assetPath.substringAfter("/").substringBefore(".mp3")
        val handler = PlatformDependencyProvider().getAudioHandler()
        (handler as AudioHandler).loadSample(instrument, note)

        return id
    }

    actual fun unloadAllSamples() {
        nativeSampleIdCache.clear()
        sampleIdMap.clear()
    }

    actual suspend fun cacheAllSamples(instruments: List<String>, onProgress: (Float) -> Unit) {
        // Essential octaves to pre-load for immediate feedback
        val essentialOctaves = listOf(3, 4)
        val allNotes = ChordUtils.ALL_NOTES
        
        val totalToLoad = instruments.size * essentialOctaves.size * 12
        var loaded = 0
        
        val handler = PlatformDependencyProvider().getAudioHandler() as AudioHandler

        for (instrument in instruments) {
            for (note in allNotes) {
                val octave = note.takeLast(1).toIntOrNull() ?: 0
                
                // Only pre-cache essential octaves to speed up startup
                if (octave in essentialOctaves) {
                    if (isNoteValidForInstrument(instrument, note)) {
                        handler.loadSample(instrument, note)
                    }
                    loaded++
                    onProgress(loaded.toFloat() / totalToLoad)
                }
            }
        }
        onProgress(1.0f)
    }

    private fun isNoteValidForInstrument(instrument: String, note: String): Boolean {
        val noteName = note.takeWhile { !it.isDigit() }
        val octave = note.takeLast(1).toIntOrNull() ?: 0
        
        return when (instrument.lowercase()) {
            "piano" -> {
                // A0 to C8
                if (octave == 0) noteName in listOf("A", "Bb", "B")
                else if (octave == 8) noteName == "C"
                else octave in 1..7
            }
            "guitar" -> {
                // F1 to E6
                if (octave == 1) noteName in listOf("F", "Gb", "G", "Ab", "A", "Bb", "B")
                else if (octave == 6) noteName == "E"
                else octave in 2..5
            }
            "bass" -> {
                // C0 to C5
                if (octave == 5) noteName == "C"
                else octave in 0..4
            }
            "drums" -> {
                // Verified drum samples from file list
                when (octave) {
                    0 -> noteName in listOf("B", "Bb")
                    1 -> noteName in listOf("B", "E", "Eb", "G")
                    4 -> noteName != "B"
                    6 -> noteName in listOf("C", "D", "Db", "Eb")
                    else -> octave in 2..5
                }
            }
            else -> true
        }
    }
    
    fun getAssetPath(id: Int): String? = sampleIdMap[id]
}
